import {useCallback, useMemo, useState, type ReactNode} from 'react';
import {ModalPdf} from '../../components/modal/modal-pdf';
import {ModalXlsx} from '../../components/modal/modal-xlsx';
import {ExportDataContext} from './context';
import {ExportDataContextType, ExportPreviewState} from './types';

export const ExportDataProvider = ({children}: {children: ReactNode}) => {
  const [state, setState] = useState<ExportPreviewState>(null);

  const closeExportPreview = useCallback(() => {
    setState(null);
  }, []);

  const previewPdf = useCallback<ExportDataContextType['previewPdf']>(async (factory) => {
    try {
      // Set loading in modal
      setState({format: 'pdf', loading: true});

      // Generate file
      const result = await factory();
      if (!result) {
        setState(null);
        return;
      }

      // Show generated file in modal
      setState({
        format: 'pdf',
        file: result.file,
        fileName: result.fileName,
        loading: false,
      });
    } catch (error) {
      setState(null);
      throw error;
    }
  }, []);

  const previewXlsx = useCallback<ExportDataContextType['previewXlsx']>(async (factory) => {
    try {
      // Set loading in modal
      setState({format: 'xlsx', loading: true});

      // Generate file
      const result = await factory();
      if (!result) {
        setState(null);
        return;
      }

      // Show generated file in modal
      setState({
        format: 'xlsx',
        file: result.file,
        fileName: result.fileName,
        loading: false,
      });
    } catch (error) {
      setState(null);
      throw error;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      previewPdf,
      previewXlsx,
      closeExportPreview,
    }),
    [closeExportPreview, previewPdf, previewXlsx]
  );

  return (
    <ExportDataContext.Provider value={contextValue}>
      {children}

      {state?.format === 'pdf' && (
        <ModalPdf open onClose={closeExportPreview} pdf={state.file} saveName={state.fileName} />
      )}

      {state?.format === 'xlsx' && (
        <ModalXlsx
          open
          onClose={closeExportPreview}
          workBook={state.file}
          saveName={state.fileName}
        />
      )}
    </ExportDataContext.Provider>
  );
};
